import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { addQuestion, updateQuestion } from '../vsecuriteSlice';
import { showSuccessToast, showErrorToast } from '../../../app/utils/helpers';
import Langs, { Localize } from '../../../app/lang/langs';

/** Brand orange used throughout this module */
const BRAND_ORANGE = '#FF9015';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

/** Maximum number of answer options allowed per question */
const MAX_OPTIONS = 6;
/** Minimum number of answer options required per question */
const MIN_OPTIONS = 2;

/**
 * Builds an empty option object for initialising new answer rows.
 * @returns {{ text: string, isCorrect: boolean, _key: number }}
 */
const createEmptyOption = () => ({
    text:      '',
    isCorrect: false,
    _key:      Date.now() + Math.random(),
});

/**
 * QuestionForm – Admin form for adding or editing a question within a V-Sécurité quiz.
 *
 * Handles dynamic answer options, enforces single/multi correctness rules,
 * and dispatches addQuestion or updateQuestion on save.
 *
 * @param {object}        props
 * @param {object|null}   props.question  - Existing question object (edit mode), or null (create mode).
 * @param {number}        props.quizId    - ID of the parent quiz.
 * @param {Function}      props.onSave    - Callback fired after a successful save.
 * @param {Function}      props.onCancel  - Callback fired when the user cancels.
 */
const QuestionForm = ({ question, quizId, onSave, onCancel }) => {
    const dispatch = useDispatch();
    const lang = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const mobile = isMobileRedux === 2 || isMobileScreen;

    /* ── Form field state ────────────────────────────────────────────────────── */
    const [texte, setTexte]             = useState('');
    const [type, setType]               = useState('single');
    const [explication, setExplication] = useState('');
    const [options, setOptions]         = useState([createEmptyOption(), createEmptyOption()]);

    /** Tracks whether the form has been submitted once (enables inline validation messages). */
    const [submitted, setSubmitted]     = useState(false);
    /** Local submitting flag to show loading state on the save button. */
    const [submitting, setSubmitting]   = useState(false);

    /* ── Populate fields when editing an existing question ───────────────────── */
    useEffect(() => {
        if (question) {
            setTexte(question.question_text || '');
            setType(question.type || 'single');
            setExplication(question.explanation || '');

            /* Rebuild options list from the question's options array */
            const rawOptions = question.options || [];

            if (rawOptions.length >= MIN_OPTIONS) {
                const sorted = [...rawOptions].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
                setOptions(
                    sorted.map((opt) => ({
                        id:        opt.id,
                        text:      opt.option_text || '',
                        isCorrect: Boolean(opt.is_correct),
                        order_index: opt.order_index,
                        _key:      opt.id ?? Date.now() + Math.random(),
                    }))
                );
            } else {
                setOptions([createEmptyOption(), createEmptyOption()]);
            }
        } else {
            /* Reset to blank state for create mode */
            setTexte('');
            setType('single');
            setExplication('');
            setOptions([createEmptyOption(), createEmptyOption()]);
        }
    }, [question]);

    /* ── Derived validation values ───────────────────────────────────────────── */

    /** Number of options marked as correct. */
    const correctCount = options.filter((o) => o.isCorrect).length;

    /** True when the question text is empty. */
    const texteError = submitted && !texte.trim();
    /** True when fewer than 2 options exist. */
    const tooFewOptionsError = submitted && options.length < MIN_OPTIONS;
    /** True when any option text is blank. */
    const emptyOptionError = submitted && options.some((o) => !o.text.trim());
    /** True when no option is marked correct. */
    const noCorrectError = submitted && correctCount === 0;
    /** True when single type has more than one correct option. */
    const singleMultiCorrectError = submitted && type === 'single' && correctCount > 1;

    /** Overall form is valid when all rules pass. */
    const isFormValid =
        texte.trim().length > 0 &&
        options.length >= MIN_OPTIONS &&
        options.every((o) => o.text.trim().length > 0) &&
        correctCount >= 1 &&
        (type !== 'single' || correctCount === 1);

    /* ── Option mutation helpers ─────────────────────────────────────────────── */

    /**
     * Appends a blank option to the list, up to MAX_OPTIONS.
     */
    const addOption = () => {
        if (options.length < MAX_OPTIONS) {
            setOptions((prev) => [...prev, createEmptyOption()]);
        }
    };

    /**
     * Removes the option at the given index, provided more than MIN_OPTIONS remain.
     * @param {number} index - Index of the option to remove.
     */
    const removeOption = (index) => {
        if (options.length > MIN_OPTIONS) {
            setOptions((prev) => prev.filter((_, i) => i !== index));
        }
    };

    /**
     * Updates the text value of a specific option.
     * @param {number} index  - Index of the option to update.
     * @param {string} value  - New text value.
     */
    const updateOptionText = (index, value) => {
        setOptions((prev) =>
            prev.map((opt, i) => (i === index ? { ...opt, text: value } : opt))
        );
    };

    /**
     * Toggles the isCorrect flag of a specific option.
     * For single-type questions, marking one correct automatically un-marks others.
     * @param {number} index - Index of the option to toggle.
     */
    const toggleOptionCorrect = (index) => {
        setOptions((prev) =>
            prev.map((opt, i) => {
                if (type === 'single') {
                    /* Single-choice: allow only one correct answer */
                    return { ...opt, isCorrect: i === index ? !opt.isCorrect : false };
                }
                /* Multi-choice: toggle freely */
                return i === index ? { ...opt, isCorrect: !opt.isCorrect } : opt;
            })
        );
    };

    /* ── Type change handler ─────────────────────────────────────────────────── */

    /**
     * Handles switching between single and multi question types.
     * When switching to single, all but the first correct option are un-marked.
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    const handleTypeChange = (e) => {
        const newType = e.target.value;
        setType(newType);

        if (newType === 'single') {
            /* Keep only the first correct option when downgrading to single */
            let firstCorrectFound = false;
            setOptions((prev) =>
                prev.map((opt) => {
                    if (opt.isCorrect && !firstCorrectFound) {
                        firstCorrectFound = true;
                        return opt;
                    }
                    return { ...opt, isCorrect: false };
                })
            );
        }
    };

    /* ── Submit handler ──────────────────────────────────────────────────────── */

    /**
     * Validates the form and dispatches addQuestion or updateQuestion.
     * Calls onSave() on success, shows a toast on failure.
     * @param {React.FormEvent} e
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        if (!isFormValid) return;

        /* Build the payload expected by the thunks */
        const payload = {
            id_quiz:      quizId,
            question_text: texte.trim(),
            type,
            explanation:  explication.trim(),
            options:      options.map((opt, idx) => ({
                id:           opt.id ?? null,
                option_text:  opt.text.trim(),
                is_correct:   opt.isCorrect ? 1 : 0,
                order_index:  idx + 1,
            })),
        };

        setSubmitting(true);
        try {
            if (question?.id) {
                await dispatch(updateQuestion({ id: question.id, ...payload })).unwrap();
                showSuccessToast(Localize('VSEC_TOAST_QUESTION_UPDATED', lang));
            } else {
                await dispatch(addQuestion(payload)).unwrap();
                showSuccessToast(Localize('VSEC_TOAST_QUESTION_ADDED', lang));
            }
            onSave();
        } catch {
            showErrorToast(Localize('VSEC_TOAST_QUESTION_SAVE_ERROR', lang));
        } finally {
            setSubmitting(false);
        }
    };

    /* ── Render ──────────────────────────────────────────────────────────────── */
    const isEditMode = Boolean(question?.id);

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: mobile ? '#fff' : 'rgba(255,255,255,0.8)',
                backdropFilter: mobile ? 'none' : 'blur(14px)',
                WebkitBackdropFilter: mobile ? 'none' : 'blur(14px)',
                borderRadius: mobile ? 0 : '20px',
                border: mobile ? 'none' : '1px solid rgba(0, 40, 128, 0.06)',
                maxWidth: mobile ? '100%' : 760,
                width: '100%',
                boxShadow: mobile ? 'none' : '0 1px 4px rgba(255, 144, 21, 0.04), 0 8px 32px rgba(0, 40, 128, 0.05)',
                overflow: 'hidden',
                minHeight: 0,
                flex: 1,
                '@keyframes qnfFadeIn': { from: { opacity: 0, transform: 'translateY(12px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                animation: mobile ? 'none' : `qnfFadeIn 400ms ${EASE} both`,
            }}
        >
            {/* ── Section title (sticky) ────────────────────────────────────────── */}
            <Box sx={{ flexShrink: 0, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a1a2e' }}>
                    {isEditMode ? <Langs str="VSEC_QUESTION_FORM_TITLE_EDIT" /> : <Langs str="VSEC_QUESTION_FORM_TITLE_CREATE" />}
                </Typography>
                <Divider />
            </Box>

            {/* ── Scrollable content area ──────────────────────────────────────── */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                px: { xs: 2, sm: 3 },
                pt: 3,
                pb: 1,
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255, 144, 21, 0.18)',
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(255, 144, 21, 0.35)',
                },
            }}>

            {/* ── Texte de la question ─────────────────────────────────────────── */}
            <TextField
                label={Localize('VSEC_FIELD_QUESTION_TEXT', lang)}
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                error={texteError}
                helperText={texteError ? Localize('VSEC_ERROR_QUESTION_TEXT_REQUIRED', lang) : ''}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 3, '& input': { fontSize: '16px' }, '& textarea': { fontSize: '16px' } }}
                inputProps={{ maxLength: 2000 }}
            />

            {/* ── Type de question ─────────────────────────────────────────────── */}
            <FormControl sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ mb: 0.5, fontSize: '0.875rem', fontWeight: 600 }}>
                    <Langs str="VSEC_QUESTION_TYPE_LABEL" />
                </FormLabel>
                <RadioGroup row={!mobile} value={type} onChange={handleTypeChange}>
                    <FormControlLabel
                        value="single"
                        control={<Radio sx={{ '&.Mui-checked': { color: BRAND_ORANGE } }} />}
                        label={Localize('VSEC_QUESTION_TYPE_SINGLE', lang)}
                        sx={{ minHeight: 44 }}
                    />
                    <FormControlLabel
                        value="multiple"
                        control={<Radio sx={{ '&.Mui-checked': { color: BRAND_ORANGE } }} />}
                        label={Localize('VSEC_QUESTION_TYPE_MULTI', lang)}
                        sx={{ minHeight: 44 }}
                    />
                </RadioGroup>
            </FormControl>

            {/* ── Explication ─────────────────────────────────────────────────── */}
            <TextField
                label={Localize('VSEC_FIELD_EXPLANATION', lang)}
                value={explication}
                onChange={(e) => setExplication(e.target.value)}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 3, '& input': { fontSize: '16px' }, '& textarea': { fontSize: '16px' } }}
                inputProps={{ maxLength: 2000 }}
            />

            {/* ── Options de réponse ───────────────────────────────────────────── */}
            <FormControl
                component="fieldset"
                error={tooFewOptionsError || emptyOptionError || noCorrectError || singleMultiCorrectError}
                sx={{ width: '100%', mb: 1 }}
            >
                <FormLabel component="legend" sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 600 }}>
                    <Langs str="VSEC_FIELD_ANSWER_OPTIONS" />
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: '#888', fontWeight: 400 }}
                    >
                        ({options.length}/{MAX_OPTIONS})
                    </Typography>
                </FormLabel>

                {/* Column headers (hidden on mobile — labels inline) */}
                {!mobile && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 90px 40px',
                            gap: 1,
                            mb: 1,
                            px: 0.5,
                        }}
                    >
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
                            {Localize('VSEC_COL_OPTION_TEXT', lang)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: '#666', fontWeight: 600, textAlign: 'center' }}
                        >
                            {Localize('VSEC_COL_CORRECT', lang)}
                        </Typography>
                        <span />
                    </Box>
                )}

                {/* Option rows */}
                {options.map((opt, index) => (
                    <Box
                        key={opt._key}
                        sx={{
                            display: mobile ? 'flex' : 'grid',
                            gridTemplateColumns: mobile ? undefined : '1fr 90px 40px',
                            flexDirection: mobile ? 'column' : undefined,
                            gap: mobile ? 0.75 : 1,
                            alignItems: mobile ? 'stretch' : 'center',
                            mb: 1.5,
                            p: mobile ? 1.5 : 1.25,
                            borderRadius: '12px',
                            background: opt.isCorrect
                                ? 'rgba(255, 144, 21, 0.06)'
                                : 'rgba(0, 40, 128, 0.02)',
                            border: '1px solid',
                            borderColor: opt.isCorrect ? 'rgba(255, 144, 21, 0.25)' : 'rgba(0,40,128,0.06)',
                            transition: `all 250ms ${EASE}`,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,40,128,0.04)',
                            },
                        }}
                    >
                        {/* Option text */}
                        <TextField
                            value={opt.text}
                            onChange={(e) => updateOptionText(index, e.target.value)}
                            placeholder={`Option ${index + 1}`}
                            size="small"
                            error={submitted && !opt.text.trim()}
                            fullWidth
                            inputProps={{ maxLength: 500 }}
                            sx={{ '& input': { fontSize: '16px' } }}
                        />

                        {/* Bottom row on mobile: correct toggle + delete */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: mobile ? 'space-between' : 'center',
                            ...(mobile && { mt: 0.25 }),
                        }}>
                            {/* Correct toggle — Radio for single, Checkbox for multi */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {type === 'single' ? (
                                    <Tooltip title={Localize('VSEC_TOOLTIP_CORRECT_ANSWER', lang)}>
                                        <Radio
                                            checked={opt.isCorrect}
                                            onChange={() => toggleOptionCorrect(index)}
                                            size="small"
                                            sx={{ '&.Mui-checked': { color: BRAND_ORANGE } }}
                                        />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title={Localize('VSEC_TOOLTIP_CORRECT_ANSWER', lang)}>
                                        <Checkbox
                                            checked={opt.isCorrect}
                                            onChange={() => toggleOptionCorrect(index)}
                                            size="small"
                                            sx={{ '&.Mui-checked': { color: BRAND_ORANGE } }}
                                        />
                                    </Tooltip>
                                )}
                                {mobile && (
                                    <Typography variant="caption" sx={{ color: opt.isCorrect ? BRAND_ORANGE : '#888', fontWeight: opt.isCorrect ? 600 : 400 }}>
                                        {Localize('VSEC_COL_CORRECT', lang)}
                                    </Typography>
                                )}
                            </Box>

                            {/* Delete option */}
                            {mobile ? (
                                <IconButton
                                    size="small"
                                    onClick={() => removeOption(index)}
                                    disabled={options.length <= MIN_OPTIONS}
                                    sx={{
                                        color: '#d32f2f',
                                        minWidth: 44,
                                        minHeight: 44,
                                        '&.Mui-disabled': { color: '#ccc' },
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            ) : (
                                <Tooltip
                                    title={
                                        options.length <= MIN_OPTIONS
                                            ? Localize('VSEC_TOOLTIP_MIN_OPTIONS', lang).replace('%n', MIN_OPTIONS)
                                            : Localize('VSEC_TOOLTIP_DELETE_OPTION', lang)
                                    }
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => removeOption(index)}
                                            disabled={options.length <= MIN_OPTIONS}
                                            sx={{
                                                color: '#d32f2f',
                                                '&.Mui-disabled': { color: '#ccc' },
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                ))}

                {/* Validation messages */}
                {tooFewOptionsError && (
                    <FormHelperText>{Localize('VSEC_ERROR_TOO_FEW_OPTIONS', lang).replace('%n', MIN_OPTIONS)}</FormHelperText>
                )}
                {emptyOptionError && (
                    <FormHelperText>{Localize('VSEC_ERROR_EMPTY_OPTIONS', lang)}</FormHelperText>
                )}
                {noCorrectError && (
                    <FormHelperText>{Localize('VSEC_ERROR_NO_CORRECT', lang)}</FormHelperText>
                )}
                {singleMultiCorrectError && (
                    <FormHelperText>{Localize('VSEC_ERROR_SINGLE_MULTI_CORRECT', lang)}</FormHelperText>
                )}
            </FormControl>

            {/* ── Add option button ────────────────────────────────────────────── */}
            <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={addOption}
                disabled={options.length >= MAX_OPTIONS}
                fullWidth={mobile}
                sx={{
                    mb: 2,
                    color: BRAND_ORANGE,
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 44,
                    '&.Mui-disabled': { color: '#ccc' },
                }}
            >
                <Langs str="VSEC_BTN_ADD_OPTION" />
                {options.length >= MAX_OPTIONS && (
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 0.5, color: '#aaa' }}
                    >
                        (max {MAX_OPTIONS})
                    </Typography>
                )}
            </Button>

            </Box>{/* end scrollable content area */}

            {/* ── Form actions (sticky footer) ─────────────────────────────────── */}
            <Box sx={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: mobile ? 'column-reverse' : 'row',
                justifyContent: 'flex-end',
                gap: mobile ? 1 : 2,
                borderTop: '1px solid rgba(0, 40, 128, 0.06)',
                px: { xs: 2, sm: 3 },
                py: 2,
                background: mobile ? '#fff' : 'rgba(255,255,255,0.85)',
                backdropFilter: mobile ? 'none' : 'blur(8px)',
                WebkitBackdropFilter: mobile ? 'none' : 'blur(8px)',
            }}>
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={submitting}
                    fullWidth={mobile}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        minHeight: 48,
                        minWidth: 110,
                        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { transform: 'scale(1.02)' },
                        '&:active': { transform: 'scale(0.98)' },
                    }}
                >
                    <Langs str="VSEC_BTN_CANCEL" />
                </Button>

                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    fullWidth={mobile}
                    sx={{
                        backgroundColor: BRAND_ORANGE,
                        color: '#fff',
                        textTransform: 'none',
                        borderRadius: '10px',
                        fontWeight: 600,
                        minHeight: 48,
                        minWidth: 140,
                        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { backgroundColor: '#e6820f', transform: 'scale(1.02)' },
                        '&:active': { transform: 'scale(0.98)' },
                        '&.Mui-disabled': { backgroundColor: '#ffc87a', color: '#fff' },
                    }}
                    startIcon={
                        submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null
                    }
                >
                    {submitting ? <Langs str="VSEC_BTN_SAVING" /> : <Langs str="VSEC_BTN_SAVE" />}
                </Button>
            </Box>
        </Box>
    );
};

export default QuestionForm;
